import app from 'flarum/forum/app';
import Modal from 'flarum/common/components/Modal';
import Button from 'flarum/common/components/Button';
import Stream from 'flarum/common/utils/Stream';
import AlertUtil from 'flarum/forum/dianLiang/alertUtil';
import type Mithril from 'mithril';

export interface IAvatarNicknameModalAttrs {
  userId: number;
}

function getDefaultAvatars(): string[] {
  const val = app.data.defaultAvatarUrl;
  if (!val) return [];
  if (Array.isArray(val)) return val;
  if (typeof val === 'string') {
    try {
      const parsed = JSON.parse(val);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [val];
    }
  }
  return [];
}

export default class AvatarNicknameModal extends Modal<IAvatarNicknameModalAttrs> {
  nickname!: Stream<string>;
  selectedAvatar!: Stream<string>;
  customAvatarFile!: File | null;
  customAvatarPreview!: Stream<string>;
  nickNameLength: number;

  oninit(vnode: Mithril.Vnode<IAvatarNicknameModalAttrs, this>) {
    super.oninit(vnode);
    const defaultAvatars = getDefaultAvatars();
    this.nickname = Stream('');
    this.selectedAvatar = Stream(defaultAvatars.length > 0 ? defaultAvatars[0] : '');
    this.customAvatarFile = null;
    this.customAvatarPreview = Stream('');
    this.nickNameLength = app.forum.attribute('nickNameLength') ? app.forum.attribute('nickNameLength') : 60;
  }

  className() {
    return 'Modal--small AvatarNicknameModal';
  }

  title() {
    return app.translator.trans('core.forum.sign_up.title');
  }

  content() {
    const previewSrc = this.customAvatarPreview() || this.selectedAvatar();

    return (
      <div className="Modal-body">
        <div className="AvatarNicknameModal-content">
          {/* 头像预览区 */}
          <div className="AvatarNicknameModal-preview">
            <div className="AvatarNicknameModal-preview-img">
              {previewSrc ? (
                <img src={previewSrc} alt="avatar" />
              ) : (
                <div className="AvatarNicknameModal-preview-placeholder">
                  <i className="fas fa-user"></i>
                </div>
              )}
            </div>
          </div>

          {/* 默认头像选择 */}
          <div className="AvatarNicknameModal-avatars">
            <div className="AvatarNicknameModal-avatars-list">
              {getDefaultAvatars().map((url) => (
                <div
                  className={`AvatarNicknameModal-avatar-item ${!this.customAvatarFile && this.selectedAvatar() === url ? 'active' : ''}`}
                  onclick={() => {
                    this.selectedAvatar(url);
                    this.customAvatarFile = null;
                    this.customAvatarPreview('');
                    m.redraw();
                  }}
                >
                  <img src={url} alt="avatar" />
                </div>
              ))}
              {/* 自定义上传按钮 */}
              <div
                className={`AvatarNicknameModal-avatar-item upload ${this.customAvatarFile ? 'active' : ''}`}
                onclick={this.openFilePicker.bind(this)}
              >
                {this.customAvatarPreview() ? (
                  <img src={this.customAvatarPreview()} alt="custom" />
                ) : (
                  <></>
                )}
              </div>
            </div>
          </div>

          {/* 昵称输入 */}
          <div className="AvatarNicknameModal-nickname">
            <div className="Form-group">
              <input
                type="text"
                className="FormControl"
                placeholder= {app.translator.trans('dl-optimization.forum.avatarNicknameModal.nickname_input')}
                bidi={this.nickname}
                maxlength={this.nickNameLength}
                disabled={this.loading}
              />
              <div className="counter">{this.nickname() ? this.nickname().length : 0}/{this.nickNameLength}</div>
            </div>
          </div>

          {/* 确认按钮 */}
          <div className="Form-group">
            <Button className="Button Button--primary Button--block" onclick={this.handleSubmit.bind(this)} loading={this.loading} disabled={!this.nickname().trim()}>
              {app.translator.trans('core.forum.sign_up.submit_button')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  openFilePicker() {
    if (this.loading) return;

    const $input = $('<input type="file" accept=".jpg, .jpeg, .png, .bmp, .gif">');
    $input
      .appendTo('body')
      .hide()
      .click()
      .on('input', (e: any) => {
        const file = e.target.files[0];
        if (!file) return;
        const validTypes = ['image/jpg', 'image/jpeg', 'image/png', 'image/bmp', 'image/gif'];
        if (validTypes.indexOf(file.type) === -1) {
          AlertUtil.onWarn(app, app.translator.trans('dl-response.forum.warn.file_limit'));
          $(e.target).remove();
          return;
        }
        // 只预览，不触发上传
        this.customAvatarFile = file;
        const reader = new FileReader();
        reader.onload = (ev: any) => {
          this.customAvatarPreview(ev.target.result);
          m.redraw();
        };
        reader.readAsDataURL(file);
        $(e.target).remove();
      });
  }

  handleSubmit() {
    if (!this.nickname().trim()) {
      AlertUtil.onWarn(app, app.translator.trans('dl-alert.forum.nickname_required') || app.translator.trans('dl-optimization.forum.avatarNicknameModal.nickname_input'));
      return;
    }

    if (this.nickname().length > this.nickNameLength) {
      AlertUtil.onWarn(app, app.translator.trans('dl-alert.forum.nickname_rule', { length: this.nickNameLength }));
      return;
    }

    if (!this.customAvatarFile && !this.selectedAvatar()) {
      AlertUtil.onWarn(app, app.translator.trans('dl-alert.forum.avatar_required') || app.translator.trans('dl-optimization.forum.avatarNicknameModal.select_avatar'));
      return;
    }

    this.loading = true;
    const userId = this.attrs.userId;
    const nickname = this.nickname().trim();

    // 统一用 FormData：默认头像传 URL 字符串，自定义头像传文件，后端自行判断
    const data = new FormData();
    data.append('data[attributes][nickname]', nickname);
    if (this.customAvatarFile) {
      data.append('avatarUrl', this.customAvatarFile);
    } else {
      data.append('data[attributes][avatarUrl]',  this.selectedAvatar());
    }

    app
      .request({
        method: 'PATCH',
        url: `${app.forum.attribute('apiUrl')}/users/${userId}`,
        // serialize: (raw: any) => raw,
        body: data,
      })
      .then(() => {
        this.loading = false;
        app.modal.close();
        window.location.reload();
      })
      .catch((error: any) => {
        this.loading = false;
        AlertUtil.onWarn(app, error.responseText || error.message);
        m.redraw();
      });
  }
}
